import { useState, type ReactNode } from 'react';
import { CaretDown, Minus, Plus } from '@phosphor-icons/react';
import { Money } from './components';
import type { Product, Sku } from './types';
import { ProductImage } from './ProductImage';
import './product-image.css';

const MAX_QUANTITY = 999_999;

type CatalogProductRowProps = {
  product: Product;
  quantityForSku: (sku: Sku) => number;
  onQuantity: (product: Product, sku: Sku, quantity: number) => void;
  canOrder: boolean;
  showSkuCode?: boolean;
  metricHeading?: string;
  renderMetric?: (sku: Sku) => ReactNode;
  renderTotal?: (sku: Sku, quantity: number) => ReactNode;
  quantityLabelForSku?: (sku: Sku) => string;
  disabled?: (sku: Sku) => boolean;
};

export function CatalogProductRow({
  product,
  quantityForSku,
  onQuantity,
  canOrder,
  showSkuCode = true,
  metricHeading = '单价',
  renderMetric,
  renderTotal,
  quantityLabelForSku,
  disabled
}: CatalogProductRowProps) {
  const [expanded, setExpanded] = useState(true);
  const grouped = (product.sourceProductCount ?? 1) > 1;
  return (
    <article className="product-row">
      <header>
        <div className="product-image"><ProductImage productId={product.id} imageUrl={product.imageUrl} /></div>
        <div className="product-info"><span className="product-code">{grouped ? '同系列 SKU 已归并' : product.code}</span><h2>{product.name}</h2><p>{grouped ? '请选择具体型号与包装规格' : product.description || '标准水管及配件系列'}</p><small>{product.categoryName}</small></div>
        <div className="product-meta"><b>{product.skus.length}</b><span>种规格</span></div>
        <button className="icon-button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} aria-label={expanded ? '收起规格' : '展开规格'}><CaretDown className={expanded ? 'rotate' : ''} /></button>
      </header>
      {expanded && <div className="sku-table">
        <div className="sku-table__head"><span>{grouped ? `型号 / 包装规格 / ${showSkuCode ? 'SKU' : '商品编码'}` : `规格 / ${showSkuCode ? 'SKU' : '商品编码'}`}</span><span>{metricHeading}</span><span className={canOrder ? 'sku-quantity-heading' : undefined}>{canOrder ? '订购数量' : '销售单位'}</span><span>{canOrder ? '小计' : ''}</span></div>
        {product.skus.map((sku) => {
          const quantity = canOrder ? quantityForSku(sku) : 0;
          return <div className={`sku-row ${quantity > 0 ? 'selected' : ''}`} key={sku.id}>
            <span className={`sku-spec${grouped ? ' sku-spec--with-image' : ''}`}>
              {grouped && sku.productId && <span className="product-image sku-product-image"><ProductImage productId={sku.productId} imageUrl={sku.imageUrl} /></span>}
              <span className="sku-spec__text">
                {showSkuCode ? <><b>{grouped ? sku.productName || sku.specification : sku.specification}</b>
                  {sku.productCode ? <><small>{grouped ? `${sku.specification} · ` : ''}{sku.unit}</small><small>商品编码 {sku.productCode} · SKU编码 {sku.code}</small></>
                    : <small>{grouped ? `${sku.specification} · ` : ''}{sku.code} · {sku.unit}</small>}</>
                  : <>{grouped && <b>{sku.productName || sku.specification}</b>}
                    <small className="sku-spec__details"><span>{grouped ? sku.specification : <b>{sku.specification}</b>} · {sku.unit}</span><span>商品编码 {sku.productCode || product.code}</span></small></>}
              </span>
            </span>
            {renderMetric ? renderMetric(sku) : <Money cents={sku.priceCents} />}
            {canOrder ? <>
              <CatalogQuantity value={quantity} unit={sku.unit} label={quantityLabelForSku?.(sku) ?? `订购数量，单位${sku.unit}`} disabled={disabled?.(sku) ?? false} onChange={(value) => onQuantity(product, sku, value)} />
              {renderTotal ? renderTotal(sku, quantity) : <Money cents={sku.priceCents * quantity} className={quantity === 0 ? 'muted' : ''} />}
            </> : <><span>{sku.unit}</span><span /></>}
          </div>;
        })}
      </div>}
    </article>
  );
}

export function CatalogQuantity({
  value,
  unit,
  onChange,
  label,
  disabled = false
}: {
  value: number;
  unit: string;
  onChange: (value: number) => void;
  label?: string;
  disabled?: boolean;
}) {
  const changeQuantity = (quantity: number) => onChange(Math.max(0, Math.min(MAX_QUANTITY, Math.floor(quantity))));
  return <span className="quantity">
    <button onClick={() => changeQuantity(value - 1)} disabled={disabled || value === 0} aria-label="减少数量"><Minus /></button>
    <input inputMode="numeric" aria-label={label ?? `订购数量，单位${unit}`} disabled={disabled} value={value || ''} placeholder="0" onChange={(event) => changeQuantity(Number(event.target.value.replace(/\D/g, '')) || 0)} />
    <small>{unit}</small>
    <button onClick={() => changeQuantity(value + 1)} disabled={disabled || value >= MAX_QUANTITY} aria-label="增加数量"><Plus /></button>
  </span>;
}
